import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { AskRequest, QueueView } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import { IconFolder, IconChevronDown } from 'components/Icons';
import { SwipeableRequestCard, SwipeAction } from './SwipeableRequestCard';

import { formatMessageContentToPlainText } from '../../Cards/MessagesCard/subcomponents/MessageContent/formatMessageContent';
import { getLastActivityDate } from '../../Panels/BrowserPanel/utils/dateGrouping';
import type { WorkState, RequestCardAssignee } from '../../Panels/BrowserPanel/components/RequestCard';
import type { User } from '@hone-automation/common';
import './MobileCollapsibleGroupCard.scss';

interface MobileCollapsibleGroupCardProps {
  group: AskRequest;
  allRequests: AskRequest[];
  activeView: QueueView | undefined;
  userId: string | null | undefined;
  selectedIds: (string | number)[];
  isSelectionMode: boolean;
  onGroupTap: (request: AskRequest) => void;
  onChildTap: (request: AskRequest) => void;
  onChildLongPress: (request: AskRequest) => void;
  onChildSwipeAction: (action: SwipeAction, request: AskRequest) => void;
  getAssignee: (request: AskRequest) => RequestCardAssignee | undefined;
  getWorkState: (request: AskRequest) => WorkState | undefined;
  checkHasMention: (request: AskRequest) => boolean;
  checkHasAttachment: (request: AskRequest) => boolean;
  isInternal: (request: AskRequest) => boolean;
  formatCardDate: (date: Date) => string;
  searchQuery: string;
  requestCardUsers?: User[];
  isAccountant?: boolean;
  totalGroupChildCount?: number;
  getShortId: (id: string) => string;
  filterChildren?: (requests: AskRequest[]) => AskRequest[];
}

export const MobileCollapsibleGroupCard: React.FC<MobileCollapsibleGroupCardProps> = ({
  group,
  allRequests,
  activeView,
  userId,
  selectedIds,
  isSelectionMode,
  onGroupTap,
  onChildTap,
  onChildLongPress,
  onChildSwipeAction,
  getAssignee,
  getWorkState,
  checkHasMention,
  checkHasAttachment,
  isInternal,
  formatCardDate,
  searchQuery,
  requestCardUsers,
  isAccountant = false,
  totalGroupChildCount,
  getShortId,
  filterChildren,
}) => {
  const prefix = prefixClass('ksq2-mobile-collapsible-group');
  const [isExpanded, setIsExpanded] = useState(false);

  // Get all children of this group
  const allChildren = useMemo(() => {
    return allRequests.filter(r => r.parentId === group.id && r.type !== 'Group');
  }, [allRequests, group.id]);

  // Apply view filtering to children based on activeView
  const viewFilteredChildren = useMemo(() => {
    if (!activeView) return allChildren;

    switch (activeView) {
      case 'Me':
        return allChildren.filter(r => r.priority !== 'Blocker' && r.status === 'Open' && r.assignee === userId);
      case 'My Team':
        return allChildren.filter(r => r.priority !== 'Blocker' && r.status === 'Open' && r.assignee !== userId);
      case 'Client':
        return allChildren.filter(r => r.priority !== 'Blocker' && r.status === 'In Progress');
      case 'Completed':
        return allChildren.filter(r => r.priority !== 'Blocker' && r.status === 'Closed');
      case 'Blockers':
        return allChildren.filter(r => r.priority === 'Blocker');
      case 'Groups':
        return allChildren;
      default:
        return allChildren;
    }
  }, [allChildren, activeView, userId]);

  // Apply toolbar filters (search, quick filters, advanced filters) to children
  const filteredChildren = useMemo(() => {
    return filterChildren ? filterChildren(viewFilteredChildren) : viewFilteredChildren;
  }, [filterChildren, viewFilteredChildren]);

  const visibleChildCount = filteredChildren.length;

  // Hide group if no children match the current view (except in Groups view)
  if (activeView !== 'Groups' && visibleChildCount === 0) {
    return null;
  }

  // Count display
  const trueTotal = totalGroupChildCount ?? viewFilteredChildren.length;
  const isCountFiltered = visibleChildCount !== trueTotal;
  const countText = isCountFiltered ? `${visibleChildCount}/${trueTotal}` : `${trueTotal}`;

  // Memo: first meaningful comment text
  const memoText = (() => {
    if (!group.comments || group.comments.length === 0) return undefined;
    for (const c of group.comments) {
      const text = c.comment;
      if (!text) continue;
      const lower = text.toLowerCase().trim();
      if (lower === 'group created' || lower === 'group created.') continue;
      return formatMessageContentToPlainText(text);
    }
    return undefined;
  })();

  const displayTitle = group.title || 'Untitled Group';

  const handleChevronTap = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  };

  const handleHeaderTap = () => {
    onGroupTap(group);
  };

  // Get display date
  const displayDate = getLastActivityDate(group);

  return (
    <div
      className={classNames(prefix(), {
        [prefix('--expanded')]: isExpanded,
      })}
      data-request-id={group.id}
    >
      {/* Header */}
      <div className={prefix('__header')}>
        <div
          className={prefix('__left-col')}
          onClick={handleChevronTap}
          role="button"
          aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
        >
          <div
            className={classNames(prefix('__chevron'), {
              [prefix('__chevron--expanded')]: isExpanded,
            })}
          >
            <IconChevronDown />
          </div>
          <div className={prefix('__folder-icon')}>
            <IconFolder />
          </div>
        </div>

        <div className={prefix('__content')} onClick={handleHeaderTap}>
          <div className={prefix('__top-row')}>
            <div className={prefix('__title')}>{displayTitle}</div>
            <div className={prefix('__top-right')}>
              <span className={prefix('__date')}>{formatCardDate(displayDate)}</span>
              <span className={prefix('__separator')}>·</span>
              <span
                className={classNames(prefix('__count-pill'), {
                  [prefix('__count-pill--circle')]: !isCountFiltered,
                })}
              >
                {countText}
              </span>
            </div>
          </div>
          {memoText && <div className={prefix('__memo')}>{memoText}</div>}
        </div>
      </div>

      {/* Children */}
      {isExpanded && (
        <div className={prefix('__children')}>
          {filteredChildren.map(request => {
            const lastActivityDate = getLastActivityDate(request);
            const childMemoText = request.comments?.[request.comments.length - 1]?.comment;
            const isSelected = selectedIds.includes(request.id);

            return (
              <div
                key={request.id}
                data-request-id={request.id}
                className={classNames(prefix('__child-wrapper'), {
                  selected: isSelected,
                })}
              >
                <SwipeableRequestCard
                  id={request.id}
                  request={request}
                  canManageQueue={isAccountant}
                  title={request.identifier || `Request #${getShortId(request.id)}`}
                  date={formatCardDate(lastActivityDate)}
                  assignee={getAssignee(request)}
                  memo={childMemoText}
                  users={requestCardUsers}
                  amount={
                    request.flaggedAmount ? parseFloat(String(request.flaggedAmount)) : request.transaction?.amount
                  }
                  workState={getWorkState(request)}
                  dueDate={request.dueDate ? new Date(request.dueDate) : undefined}
                  hasMention={checkHasMention(request)}
                  hasAttachment={checkHasAttachment(request)}
                  isInternal={isInternal(request)}
                  isFocused={false}
                  isSelected={isSelected}
                  searchQuery={searchQuery}
                  activeView={activeView}
                  selectedIds={selectedIds}
                  isSelectionMode={isSelectionMode}
                  onTap={() => onChildTap(request)}
                  onLongPress={() => onChildLongPress(request)}
                  onSwipeAction={action => onChildSwipeAction(action, request)}
                  dragDisabled
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
